import React, { useCallback, useEffect, useState } from "react";
import { AiFillDelete, AiOutlineTeam, AiOutlineUserAdd } from "react-icons/ai";
import UserSearch from "./UserSearch";
import UserList from "./UserList";
import UserEdit from "./UserEdit";
import DeletedUserList from "./DeletedUserList";
import Sidebar from "../Sidebar";
import Navbar from "../navbar";

const User = () => {
  const [users, setUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("users");
  const [selectedDeletedUser, setSelectedDeletedUser] = useState(null);

  const subscriptionStatuses = ["Active", "Expired", "Cancelled"];

  const fetchUsers = useCallback(async (searchTerm = "", status = "", customerIdValue = "") => {
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`/api/users?search=${searchTerm}&status=${status}&customerId=${customerIdValue}`);
      const data = await response.json();
      const userList = Array.isArray(data) ? data : [];
      setUsers(userList);
      setMessage(userList.length === 0 ? "No users found." : `${userList.length} user(s) found.`);
    } catch (error) {
      console.error("Error fetching users:", error);
      setMessage("Error fetching users.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDeletedUsers = useCallback(async () => {
    try {
      const response = await fetch(`/api/deleted-users`);
      const data = await response.json();
      setDeletedUsers(data);
    } catch (error) {
      console.error("Error fetching deleted users:", error);
    }
  }, []);

  const fetchDeletedUserById = useCallback(async (userId) => {
    try {
      const response = await fetch(`/api/deleted-users/${userId}`);
      const data = await response.json();

      if (response.ok) {
        setSelectedDeletedUser(data);
      } else {
        console.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching deleted user:", error);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchDeletedUsers();
  }, [fetchDeletedUsers, fetchUsers]);

  const refreshUsers = useCallback(() => {
    return fetchUsers(search, statusFilter, customerId);
  }, [customerId, fetchUsers, search, statusFilter]);

  const activeUsers = users.filter((item) => item?.subscriber?.subscriptionStatus === "Active").length;
  const blockedUsers = users.filter((item) => item?.user?.blockStatus === "Blocked").length;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900">
      <div className="flex min-h-screen flex-col md:flex-row">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Navbar />

          <main className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
            <section className="mb-6 flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm lg:flex-row lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-sky-600">User management</p>
                <h1 className="mt-2 text-2xl font-bold tracking-tight text-slate-950 sm:text-3xl">
                  Admin Users
                </h1>
                <p className="mt-1 max-w-2xl text-sm text-slate-500">
                  Search, review, edit, restore, and remove learner accounts from one focused workspace.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 sm:min-w-[420px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase text-slate-500">Total</p>
                  <p className="mt-1 text-2xl font-bold text-slate-950">{users.length}</p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
                  <p className="text-xs font-semibold uppercase text-emerald-700">Active</p>
                  <p className="mt-1 text-2xl font-bold text-emerald-800">{activeUsers}</p>
                </div>
                <div className="rounded-lg border border-rose-100 bg-rose-50 p-3">
                  <p className="text-xs font-semibold uppercase text-rose-700">Blocked</p>
                  <p className="mt-1 text-2xl font-bold text-rose-800">{blockedUsers}</p>
                </div>
              </div>
            </section>

            <UserSearch
              search={search}
              setSearch={setSearch}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              customerId={customerId}
              setCustomerId={setCustomerId}
              subscriptionStatuses={subscriptionStatuses}
              fetchUsers={fetchUsers}
              loading={loading}
              message={message}
            />

            <section className="mb-6 rounded-lg border border-slate-200 bg-white p-1.5 shadow-sm">
              <nav className="grid gap-2 sm:flex">
                <button
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === "users"
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                  onClick={() => setActiveTab("users")}
                >
                  <AiOutlineTeam className="text-lg" />
                  User List
                </button>
                <button
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
                    activeTab === "deletedUsers"
                      ? "bg-slate-950 text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-950"
                  }`}
                  onClick={() => setActiveTab("deletedUsers")}
                >
                  <AiFillDelete className="text-lg" />
                  Deleted Users
                </button>
                <div className="ml-auto hidden items-center gap-2 rounded-lg bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-700 ring-1 ring-sky-100 sm:flex">
                  <AiOutlineUserAdd className="text-lg" />
                  {users.length + deletedUsers.length} records loaded
                </div>
              </nav>
            </section>

            {activeTab === "users" && (
              <div className="grid gap-6">
                <UserList
                  filteredUsers={users}
                  loading={loading}
                  setSelectedUser={setSelectedUser}
                  onRefresh={refreshUsers}
                />
                {selectedUser && (
                  <UserEdit
                    selectedUser={selectedUser}
                    onUpdated={async () => {
                      await refreshUsers();
                      setSelectedUser(null);
                    }}
                  />
                )}
              </div>
            )}

            {activeTab === "deletedUsers" && (
              <div className="grid gap-6">
                <DeletedUserList
                  deletedUsers={deletedUsers}
                  fetchUsers={fetchUsers}
                  setDeletedUsers={setDeletedUsers}
                  fetchDeletedUserById={fetchDeletedUserById}
                  setSelectedDeletedUser={setSelectedDeletedUser}
                />
                {selectedDeletedUser && <UserEdit selectedUser={selectedDeletedUser} />}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default User;
